// Tier-2 <Text> (FORT-182): typographic scale + semantic colour, all from tokens.
// Variant picks the element and size/weight; colour maps to a token. No raw
// font sizes or hex at the call site.
import React from 'react';
import { useTokens } from '../../core/theme/TokensProvider';
import type { Theme } from '../../core/theme/buildTokens';
import type { CommonProps } from './types';

export type TextVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'caption'
  | 'label';

export type TextColor = 'primary' | 'secondary' | 'disabled' | 'danger' | 'success' | 'warning';
export type TextWeight = keyof Theme['fontWeight'];

export interface TextProps extends CommonProps {
  variant?: TextVariant;
  color?: TextColor;
  weight?: TextWeight;
  /** Override the rendered element (defaults derive from the variant). */
  as?: React.ElementType;
  children: React.ReactNode;
}

interface VariantSpec {
  el: React.ElementType;
  size: keyof Theme['fontSize'];
  weight: TextWeight;
  color?: TextColor;
}

const VARIANTS: Record<TextVariant, VariantSpec> = {
  h1: { el: 'h1', size: '2xl', weight: 'bold' },
  h2: { el: 'h2', size: 'xl', weight: 'bold' },
  h3: { el: 'h3', size: 'lg', weight: 'semibold' },
  h4: { el: 'h4', size: 'md', weight: 'semibold' },
  h5: { el: 'h5', size: 'sm', weight: 'semibold' },
  h6: { el: 'h6', size: 'xs', weight: 'semibold' },
  body: { el: 'span', size: 'sm', weight: 'regular' },
  caption: { el: 'span', size: 'xs', weight: 'regular', color: 'secondary' },
  label: { el: 'label', size: 'sm', weight: 'medium' },
};

const resolveColor = (tokens: Theme, color: TextColor): string => {
  switch (color) {
    case 'primary':
      return tokens.color.text.primary;
    case 'secondary':
      return tokens.color.text.secondary;
    case 'disabled':
      return tokens.color.text.disabled;
    case 'danger':
      return tokens.color.danger;
    case 'success':
      return tokens.color.success;
    case 'warning':
      return tokens.color.warning;
  }
};

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  color,
  weight,
  as,
  children,
  className,
  style,
  'data-testid': dataTestId,
}) => {
  const tokens = useTokens();
  const spec = VARIANTS[variant];
  const Element: React.ElementType = as ?? spec.el;
  const resolvedColor = resolveColor(tokens, color ?? spec.color ?? 'primary');
  return (
    <Element
      className={className}
      data-testid={dataTestId}
      style={{
        margin: 0,
        fontSize: tokens.fontSize[spec.size],
        fontWeight: tokens.fontWeight[weight ?? spec.weight],
        color: resolvedColor,
        ...style,
      }}
    >
      {children}
    </Element>
  );
};
